import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Shield, Info, Phone, X } from "lucide-react";
import { useLanguage } from "../components/LanguageContext";
import SEO from "../components/SEO";
import ImagePlaceholder from "../components/ImagePlaceholder";
import { isAiImage } from "../lib/utils";

export default function Horses() {
  const { pathname } = useLocation();
  const { lang, t } = useLanguage();
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedHorse, setSelectedHorse] = useState(null);

  // Map URL path to CMS term slug
  const isSalesRoute = pathname.includes("/salg") || pathname.includes("/sales");
  const cmsCategoryType = isSalesRoute ? "salg" : "sportsheste";

  useEffect(() => {
    const fetchHorses = async () => {
      setLoading(true);
      try {
        const termFilter = cmsCategoryType ? `?term=category:${cmsCategoryType}` : "";
        const response = await fetch(
          `https://api.dev.auxo.dk/public/v2/content/horses${termFilter}`
        );
        if (!response.ok) {
          throw new Error(lang === "da" ? "Kunne ikke hente heste fra CMS" : "Could not fetch horses from CMS");
        }
        const data = await response.json();
        setHorses(data.items || []);
      } catch (err) {
        console.error("Fejl ved hentning af heste:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchHorses();
  }, [cmsCategoryType, lang]);

  const isSport = cmsCategoryType === "sportsheste";
  const title = isSport ? t.horses.title_sport : t.horses.title_sales;
  const subtitle = isSport ? t.horses.desc_sport : t.horses.desc_sales;
  const pageKey = isSport ? "horses_sport" : "horses_sales";

  // Helper to calculate birth year from age
  const getBirthYear = (age) => {
    if (!age) return "N/A";
    const currentYear = new Date().getFullYear();
    return currentYear - age;
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] text-stone-900 font-sans-custom">
      <SEO pageKey={pageKey} />

      {/* Hero Section - Ashford Farm Style */}
      <div className="bg-stone-100/70 py-24 px-6 sm:px-12 text-center border-b border-stone-200/40">
        <div className="max-w-4xl mx-auto space-y-4">
          <span className="text-[11px] uppercase tracking-[0.3em] text-stone-500 font-semibold block">
            {isSport ? "EXCLUSIVE SPORT HORSES" : "HIGH QUALITY COLLECTION"}
          </span>
          <h1 className="font-display text-3xl sm:text-5xl md:text-6xl uppercase tracking-[0.15em] text-stone-900 font-light">
            {title} <span className="font-normal italic text-stone-600 lowercase font-display normal-case">{t.horses.title_suffix}</span>
          </h1>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white py-16 px-6 sm:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Loading / Error States */}
          {loading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="h-8 w-8 animate-spin border-2 border-stone-800 border-t-transparent rounded-none" />
            </div>
          ) : error ? (
            <div className="border border-red-200 bg-red-50/50 p-6 text-center text-red-600 rounded-none">
              {t.horses.error}: {error}.
            </div>
          ) : horses.length === 0 ? (
            <div className="border border-stone-200 bg-stone-50 p-12 text-center text-stone-500 rounded-none">
              {t.horses.empty}
            </div>
          ) : (
            /* Grid of Horses - 4 Columns on Desktop */
            <div className="grid gap-x-6 gap-y-12 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
              {horses.map((horse) => (
                <div
                  key={horse.id}
                  onClick={() => setSelectedHorse(horse)}
                  className="group cursor-pointer flex flex-col text-center"
                >
                  {/* Image Container */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-stone-100 border border-stone-200/40">
                    {horse.images && horse.images[0] && !isAiImage(horse.images[0]) ? (
                      <img
                        src={`${horse.images[0]}?width=600&quality=85&format=auto`}
                        alt={horse.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      />
                    ) : (
                      <ImagePlaceholder icon="fa-light fa-horse" text={horse.name} />
                    )}
                  </div>

                  {/* Centered Content */}
                  <h3 className="uppercase font-bold text-sm sm:text-base tracking-wider pt-4 text-stone-900 group-hover:text-stone-600 transition-colors duration-300">
                    {horse.name}
                  </h3>
                  <p className="text-xs italic text-stone-500 mt-1 font-light">
                    {horse.pedigree || "N/A"}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 mt-2 font-medium">
                    {getBirthYear(horse.age)} | {horse.gender || "N/A"} | {horse.height || "N/A"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Elegant Detail Modal */}
      {selectedHorse && (
        <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6 md:p-10 transition-opacity duration-300">
          <div className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto relative text-stone-900 flex flex-col md:flex-row border border-stone-200 shadow-2xl animate-in fade-in zoom-in-95 duration-300">
            {/* Close Button */}
            <button
              onClick={() => setSelectedHorse(null)}
              className="absolute top-4 right-4 text-stone-500 hover:text-stone-900 z-10 p-2 bg-white/80 backdrop-blur-sm transition-colors duration-200 cursor-pointer"
              aria-label="Close modal"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Left Side: Image */}
            <div className="w-full md:w-1/2 aspect-[4/3] md:aspect-auto md:min-h-[450px] bg-stone-100 relative overflow-hidden">
              {selectedHorse.images && selectedHorse.images[0] && !isAiImage(selectedHorse.images[0]) ? (
                <img
                  src={`${selectedHorse.images[0]}?width=800&quality=85&format=auto`}
                  alt={selectedHorse.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImagePlaceholder icon="fa-light fa-horse" text={selectedHorse.name} />
              )}
            </div>

            {/* Right Side: Content */}
            <div className="w-full md:w-1/2 p-8 sm:p-10 md:p-12 flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-semibold">
                  {isSport ? t.horses.title_sport : t.horses.title_sales} Collection
                </div>
                <h2 className="font-display text-2xl sm:text-3xl md:text-4xl uppercase tracking-wider text-stone-900 font-medium">
                  {selectedHorse.name}
                </h2>
                <span className="inline-block border border-stone-200 bg-stone-50 px-3 py-1 text-[10px] uppercase tracking-widest font-semibold text-stone-800">
                  {selectedHorse.price}
                </span>

                {/* Specs Grid */}
                <div className="grid grid-cols-2 gap-4 border-t border-b border-stone-100 py-4 text-[10px] uppercase tracking-widest text-stone-500 font-sans-custom">
                  <div>
                    <span className="text-stone-400 block mb-1 font-semibold">{t.horses.spec_pedigree}</span>
                    <span className="font-medium text-stone-800">{selectedHorse.pedigree || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-stone-400 block mb-1 font-semibold">{t.horses.spec_gender}</span>
                    <span className="font-medium text-stone-800">{selectedHorse.gender || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-stone-400 block mb-1 font-semibold">{t.horses.spec_age}</span>
                    <span className="font-medium text-stone-800">
                      {selectedHorse.age ? `${selectedHorse.age} ${t.horses.age_suffix}` : "N/A"} ({getBirthYear(selectedHorse.age)})
                    </span>
                  </div>
                  <div>
                    <span className="text-stone-400 block mb-1 font-semibold">{t.horses.spec_height}</span>
                    <span className="font-medium text-stone-800">{selectedHorse.height || "N/A"}</span>
                  </div>
                </div>

                {/* Description */}
                <div
                  className="text-xs leading-relaxed text-stone-600 font-light tracking-wide-custom font-sans-custom prose prose-sm max-h-[150px] overflow-y-auto pr-2"
                  dangerouslySetInnerHTML={{ __html: selectedHorse.description }}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Link
                  to={lang === "da" ? "/da/kontakt" : "/en/contact"}
                  state={{ horseName: selectedHorse.name }}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-stone-800 hover:bg-stone-900 text-stone-50 px-4 py-3 text-[10px] sm:text-xs font-semibold uppercase tracking-widest transition-colors duration-300 rounded-none cursor-pointer font-sans-custom"
                >
                  <Info className="h-4 w-4" />
                  <span>{t.horses.btn_inquiry}</span>
                </Link>
                <a
                  href="tel:+4552174997"
                  className="inline-flex items-center justify-center border border-stone-300 bg-white text-stone-700 hover:border-stone-800 hover:text-stone-900 px-4 py-3 transition-colors duration-300 rounded-none cursor-pointer"
                  title={t.horses.btn_call}
                >
                  <Phone className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
